import * as React from "react";
import { ShimmerEffect } from "@/components/ui/shimmerEffect";
import { clsx } from "clsx";

const DriveHeaderSkeleton: React.FC = () => (
  <div className="mb-6 pb-4">
    <div className="mb-2 flex flex-col justify-between space-y-2 sm:flex-row sm:space-y-0">
      <div className="h-8 w-48 rounded bg-gray-200">
        <ShimmerEffect />
      </div>
      <div className="flex space-x-2 sm:space-x-4">
        <div className="h-10 w-24 rounded bg-gray-200">
          <ShimmerEffect />
        </div>
        <div className="h-10 w-24 rounded bg-gray-200">
          <ShimmerEffect />
        </div>
      </div>
    </div>
    <div className="h-6 w-full rounded bg-gray-200">
      <ShimmerEffect />
    </div>
  </div>
);

const DriveSearchSkeleton: React.FC = () => (
  <div className="mb-4 flex items-center">
    <div className="relative flex-grow">
      <div className="h-10 w-full rounded bg-gray-200">
        <ShimmerEffect />
      </div>
    </div>
    <div className="ml-2 h-10 w-24 rounded bg-gray-200">
      <ShimmerEffect />
    </div>
  </div>
);

const DriveRowSkeleton: React.FC = () => (
  <div
    role="row"
    data-testid="drive-skeleton-row"
    className="grid grid-cols-[2rem_minmax(0,1fr)_2rem] items-center gap-3 px-4 py-3 md:grid-cols-[2rem_minmax(16rem,1.5fr)_8rem_6rem_2rem] xl:grid-cols-[2rem_minmax(16rem,1fr)_9rem_7rem_7rem_minmax(34rem,auto)_2rem]"
  >
    <div role="cell" className="h-4 w-4 rounded bg-gray-200">
      <ShimmerEffect />
    </div>
    <div role="cell" className="min-w-0">
      <div className="mb-2 h-5 w-3/4 rounded bg-gray-200">
        <ShimmerEffect />
      </div>
      <div className="h-3 w-1/2 rounded bg-gray-200">
        <ShimmerEffect />
      </div>
    </div>
    <div role="cell" className="hidden h-4 w-20 rounded bg-gray-200 md:block">
      <ShimmerEffect />
    </div>
    <div role="cell" className="hidden h-5 w-16 rounded bg-gray-200 md:block">
      <ShimmerEffect />
    </div>
    <div role="cell" className="hidden h-4 w-16 rounded bg-gray-200 xl:block">
      <ShimmerEffect />
    </div>
    <div role="cell" className="hidden h-8 rounded bg-gray-200 xl:block">
      <ShimmerEffect />
    </div>
    <div role="cell" className="h-8 w-8 rounded bg-gray-200">
      <ShimmerEffect />
    </div>
  </div>
);

interface Props {
  className?: string;
  hideHeader?: boolean;
  hideSearch?: boolean;
}

export const DriveSkeleton: React.FC<Props> = ({ className = "", hideHeader, hideSearch }) => (
  <div className={clsx("w-full", className)}>
    {!hideHeader && <DriveHeaderSkeleton />}
    {!hideSearch && <DriveSearchSkeleton />}
    <div className="max-w-full overflow-x-auto rounded-lg">
      <div
        role="table"
        aria-label="Loading documents"
        className="min-w-[36rem] overflow-hidden rounded-lg border border-desert-sand-200 bg-white xl:min-w-[84rem]"
      >
        <div
          role="row"
          className="hidden grid-cols-[2rem_minmax(16rem,1.5fr)_8rem_6rem_2rem] items-center gap-3 border-b border-desert-sand-200 bg-ivory-mist-400 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 md:grid xl:grid-cols-[2rem_minmax(16rem,1fr)_9rem_7rem_7rem_minmax(34rem,auto)_2rem]"
        >
          <span role="columnheader" />
          <span role="columnheader">Name</span>
          <span role="columnheader">Owner</span>
          <span role="columnheader">Access</span>
          <span role="columnheader" className="hidden xl:block">
            Created
          </span>
          <span role="columnheader" className="hidden text-center xl:block">
            Actions
          </span>
          <span aria-hidden="true" />
        </div>
        <div role="rowgroup" className="divide-y divide-gray-200">
          {Array.from({ length: 6 }, (_, index) => (
            <DriveRowSkeleton key={index} />
          ))}
        </div>
      </div>
    </div>
  </div>
);
